import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardCheck,
  ListTodo,
  UserPlus,
  ShoppingCart,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import TaskDashboardSummary from "../../shared/tasks/components/TaskDashboardSummary";

function looksLikeEmail(s) {
  return typeof s === "string" && s.includes("@");
}

/** Prefer real names; never treat an email string as a display name. */
function firstNameFromDisplayName(displayName) {
  if (!displayName || typeof displayName !== "string") return null;
  const t = displayName.trim();
  if (!t || looksLikeEmail(t)) return null;
  const first = t.split(/\s+/)[0];
  return first || null;
}

function pickFullNameFromProfile(userRow) {
  if (!userRow) return null;
  const raw = userRow.full_name?.trim();
  if (raw && !looksLikeEmail(raw)) return raw;
  return null;
}

const shortcuts = [
  {
    to: "/attendance",
    title: "Attendance",
    description: "Mark and review attendance records.",
    icon: ClipboardCheck,
    accent: "from-emerald-500/15 to-teal-500/10",
    ring: "ring-emerald-500/20",
    iconBg: "bg-emerald-500/15 text-emerald-700",
  },
  {
    to: "/tasks",
    title: "Tasks",
    description: "Your team tasks and follow-ups.",
    icon: ListTodo,
    accent: "from-violet-500/15 to-indigo-500/10",
    ring: "ring-violet-500/20",
    iconBg: "bg-violet-500/15 text-violet-700",
  },
  {
    to: "/crm",
    title: "CRM",
    description: "Leads and customer relationships.",
    icon: UserPlus,
    accent: "from-sky-500/15 to-blue-500/10",
    ring: "ring-sky-500/20",
    iconBg: "bg-sky-500/15 text-sky-800",
  },
  {
    to: "/orders",
    title: "Orders",
    description: "Orders and fulfilment.",
    icon: ShoppingCart,
    accent: "from-amber-500/15 to-orange-500/10",
    ring: "ring-amber-500/20",
    iconBg: "bg-amber-500/15 text-amber-800",
  },
];

const StaffHomeDashboard = () => {
  const user = getDecryptedUserFromCookie();
  const [greetingName, setGreetingName] = useState(() =>
    firstNameFromDisplayName(user?.full_name)
  );

  useEffect(() => {
    const mainId = user?.main_id;
    if (!mainId) return;

    const url = process.env.REACT_APP_LINK;
    const token = decryptToken("accessToken") || "";

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${url}/register/single/${mainId}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const res = await response.json();
        if (!response.ok || res?.isError || cancelled) {
          if (!cancelled) {
            setGreetingName(firstNameFromDisplayName(pickFullNameFromProfile(user)) || null);
          }
          return;
        }
        const fromApi = pickFullNameFromProfile(res?.users);
        const fromCookie = pickFullNameFromProfile(user);
        const resolved = fromApi || fromCookie;
        setGreetingName(firstNameFromDisplayName(resolved) || null);
      } catch {
        if (!cancelled) {
          setGreetingName(firstNameFromDisplayName(pickFullNameFromProfile(user)) || null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.main_id, user?.full_name]);

  const helloTarget = greetingName || "there";

  return (
    <div className="min-h-[calc(100vh-6rem)] w-full">
      <div className="relative overflow-hidden rounded-2xl border border-line bg-gradient-to-br from-surface-2 via-surface to-brand-indigo/10 px-5 py-8 shadow-sm sm:px-8 sm:py-10">
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-brand-cyan/10 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-brand-violet/10 blur-3xl"
          aria-hidden
        />

        <header className="relative mb-10 max-w-2xl">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-line bg-surface-2/80 px-3 py-1 text-xs font-medium text-ink-secondary shadow-sm backdrop-blur">
            <Sparkles className="size-3.5 text-warning" strokeWidth={2} />
            Staff workspace
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
            Hello, {helloTarget}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-ink-secondary sm:text-base">
            Jump into your day, pick a module below.
          </p>
        </header>

        <TaskDashboardSummary
          apiUrl={process.env.REACT_APP_LINK}
          getToken={() => decryptToken("accessToken") || ""}
          role="staff"
          className="relative mb-8"
        />

        <ul className="relative grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-5">
          {shortcuts.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={`group flex min-h-[7.5rem] flex-col justify-between rounded-xl border border-line bg-gradient-to-br ${item.accent} p-5 shadow-sm ring-1 ${item.ring} transition-all duration-200 hover:-translate-y-0.5 hover:border-line-strong hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary`}
                >
                  <div className="flex items-start gap-4">
                    <span
                      className={`flex size-12 shrink-0 items-center justify-center rounded-xl ${item.iconBg} transition-transform duration-200 group-hover:scale-105`}
                    >
                      <Icon className="size-6" strokeWidth={2} aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-lg font-semibold text-ink">{item.title}</h2>
                      <p className="mt-1 text-sm text-ink-secondary">{item.description}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-end text-sm font-medium text-info">
                    Open
                    <ChevronRight
                      className="ml-0.5 size-4 transition-transform duration-200 group-hover:translate-x-0.5"
                      strokeWidth={2}
                      aria-hidden
                    />
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default StaffHomeDashboard;
